import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame, LogOut, RefreshCw, Pencil, Check, Truck, Clock, Gauge, Trash2 } from "lucide-react";

const STATUS_OPTIONS = ["pending", "scheduled", "delivered", "cancelled"];
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_heat-dispatch/artifacts/5ljah6z6_image.png";
const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  scheduled: "bg-blue-100 text-blue-900 border-blue-300",
  delivered: "bg-emerald-100 text-emerald-900 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-900 border-rose-300",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editPrice, setEditPrice] = useState(false);
  const [priceInput, setPriceInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s, p] = await Promise.all([
        api.get(`/admin/orders${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/stats"),
        api.get("/pricing"),
      ]);
      setOrders(o.data); setStats(s.data); setPricing(p.data);
      setPriceInput(String(p.data.price_per_gallon));
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("mcfly_admin_token"); nav("/admin/login"); }
      else toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  }, [filter, nav]);

  useEffect(() => {
    if (!localStorage.getItem("mcfly_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (id, name) => {
    if (!window.confirm(`Delete order from ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const savePrice = async () => {
    const v = parseFloat(priceInput);
    if (!v || v <= 0) { toast.error("Invalid price"); return; }
    try {
      const { data } = await api.put("/admin/pricing", { price_per_gallon: v });
      setPricing(data); setEditPrice(false); toast.success("Price updated");
    } catch { toast.error("Failed to update price"); }
  };

  const logout = () => {
    localStorage.removeItem("mcfly_admin_token");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard-page">
      {/* Sidebar */}
      <aside className="w-64 bg-brand-navy text-white flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center gap-2">
          <img src={LOGO_URL} alt="McFly's Fuel Inc." className="h-10 w-auto block" />
          <div>
            <div className="font-heading text-lg leading-tight">McFly's</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold">Dispatch Panel</div>
          </div>
        </div>
        <nav className="p-4 space-y-1 text-sm flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 px-3 pb-2">Orders</div>
          {[
            { k: "", label: "All" },
            ...STATUS_OPTIONS.map((s) => ({ k: s, label: s.charAt(0).toUpperCase()+s.slice(1) })),
          ].map((f) => (
            <button key={f.k||"all"} onClick={()=>setFilter(f.k)}
              className={`w-full text-left px-3 py-2 transition-colors ${filter===f.k?"bg-brand-red text-white":"hover:bg-white/5 text-white/80"}`}
              data-testid={`filter-${f.k||"all"}`}>
              {f.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="m-4 flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30" data-testid="admin-logout">
          <LogOut size={14} /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-red font-bold">Dashboard</div>
            <h1 className="font-heading text-3xl">Today at McFly's</h1>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2 hover:border-brand-red" data-testid="refresh-btn">
            <RefreshCw size={14} className={loading?"animate-spin":""} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Stat label="Total Orders" value={stats?.total_orders ?? "—"} Icon={Truck} />
          <Stat label="Pending" value={stats?.pending ?? "—"} Icon={Clock} />
          <Stat label="Gallons Ordered" value={stats?.total_gallons_ordered ?? "—"} Icon={Gauge} />
        </div>

        {/* Pricing */}
        <div className="bg-white border border-border p-6 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="pricing-card">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-red font-bold">Live Price</div>
            <div className="font-heading text-3xl">
              {editPrice ? (
                <input value={priceInput} onChange={(e)=>setPriceInput(e.target.value)} className="border border-input px-3 py-1 w-32" data-testid="price-input" />
              ) : (
                `$${pricing?.price_per_gallon?.toFixed(2) ?? "—"}`
              )}
              <span className="text-sm text-muted-foreground font-body ml-2">/ gallon</span>
            </div>
          </div>
          {editPrice ? (
            <div className="flex gap-2">
              <button onClick={savePrice} className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 font-bold" data-testid="price-save"><Check size={14} /> Save</button>
              <button onClick={()=>setEditPrice(false)} className="border border-border px-4 py-2">Cancel</button>
            </div>
          ) : (
            <button onClick={()=>setEditPrice(true)} className="inline-flex items-center gap-2 border border-border px-4 py-2 hover:border-brand-red" data-testid="price-edit"><Pencil size={14} /> Update price</button>
          )}
        </div>

        {/* Orders table */}
        <div className="bg-white border border-border overflow-hidden" data-testid="orders-table-wrap">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-navy text-white text-left">
                <tr>
                  {["Customer","Contact","Address","Gallons","Total","Payment","Status","Date",""].map(h=>(
                    <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-14 text-muted-foreground" data-testid="orders-empty">No orders yet. When customers submit a delivery request it'll show up here.</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-border hover:bg-muted/40" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-semibold">{o.full_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div>{o.phone}</div>
                      <div className="text-xs">{o.email}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.street_address}, {o.city} {o.zip_code}</td>
                    <td className="px-4 py-3">{o.gallons}</td>
                    <td className="px-4 py-3 font-semibold">${o.total_amount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs">{o.payment_method === "stripe" ? "Online" : "On Delivery"}</div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${o.payment_status==="paid"?"text-emerald-700":"text-amber-700"}`}>{o.payment_status}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={o.status} onChange={(e)=>updateStatus(o.id, e.target.value)}
                        className={`border px-2 py-1 text-xs font-bold uppercase tracking-wider ${STATUS_STYLE[o.status]}`}
                        data-testid={`status-select-${o.id}`}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteOrder(o.id, o.full_name)}
                        className="p-1.5 text-rose-700 hover:bg-rose-100 transition-colors"
                        title="Delete order"
                        data-testid={`delete-order-${o.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value, Icon }) => (
  <div className="bg-white border border-border p-5 flex items-start justify-between">
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
    <div className="w-9 h-9 bg-brand-navy text-white flex items-center justify-center"><Icon size={16} /></div>
  </div>
);
